import React, { useState, useEffect, useMemo } from 'react';
import { DAY_LABELS, COLOR_CLASS } from '../utils/calendarConstants';
import { getDateRange, createCell } from '../utils/calendarUtils';
import { getTimeDisplay, getTimeRangeTooltip } from '../utils/calendarUtils';
import './WeekView.css';

function WeekView({ entries, offset, onCellHover, onCellLeave, onCellClick, selectedDate, onToggleEntry, onUpdateEntry, onDeleteEntry, onAddEntry }) {
  const { start, end } = getDateRange('week', offset);
  
  // Generate 7 days for the week (Sunday to Saturday)
  const days = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay() + (offset * 7));
    weekStart.setHours(0, 0, 0, 0);
    
    const daysArray = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      date.setHours(0, 0, 0, 0);
      
      // Create ISO date string manually to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const isoDate = `${year}-${month}-${day}`;
      
      const cell = createCell(date, entries, start, end);
      daysArray.push({
        ...cell,
        isoDate, // Override with manually created ISO date
        dayName: DAY_LABELS[date.getDay()],
        dayNumber: date.getDate(),
        monthName: date.toLocaleDateString('en-US', { month: 'short' })
      });
    }
    return daysArray;
  }, [offset, entries, start, end]);
  
  // State for selected day and entry management
  const [selectedDay, setSelectedDay] = useState(null);
  const [draftEntry, setDraftEntry] = useState('');
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [hasTimeSet, setHasTimeSet] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editText, setEditText] = useState('');
  const [editStartTime, setEditStartTime] = useState(null);
  const [editEndTime, setEditEndTime] = useState(null);
  
  // Update selected day when days change - maintain selection if possible
  useEffect(() => {
    if (days.length > 0) {
      // If we have a selected day, try to find it in the new week
      if (selectedDay) {
        const foundDay = days.find(d => d.isoDate === selectedDay);
        if (foundDay) {
          // Selected day exists in new week, keep it
          return;
        }
      }
      // Otherwise, select today if available, or first day
      const todayDay = days.find(d => d.isToday);
      setSelectedDay(todayDay ? todayDay.isoDate : days[0].isoDate);
    }
  }, [days, selectedDay]);
  
  // Initialize time when selected day changes
  useEffect(() => {
    if (selectedDay) {
      const now = new Date();
      const minutes = now.getMinutes();
      const roundedMinutes = Math.round(minutes / 15) * 15;
      const roundedTime = new Date(now);
      roundedTime.setMinutes(roundedMinutes, 0, 0);
      
      const startHour = roundedTime.getHours();
      const startMin = roundedTime.getMinutes();
      const endHour = (startHour + 1) % 24;
      
      setStartTime(`${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`);
      setEndTime(`${String(endHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`);
      setHasTimeSet(false);
    }
  }, [selectedDay]);
  
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let min = 0; min < 60; min += 15) {
        const timeStr = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
        options.push(timeStr);
      }
    }
    return options;
  };
  
  const timeOptions = generateTimeOptions();
  
  const handleDayClick = (day) => {
    setSelectedDay(day.isoDate);
    setEditingEntry(null);
    setDraftEntry('');
  };
  
  const handleTimeIconClick = (e) => {
    e.stopPropagation();
    setShowTimePicker(!showTimePicker);
  };
  
  const handleTimeChange = (newStartTime, newEndTime) => {
    setStartTime(newStartTime);
    setEndTime(newEndTime);
    if (newStartTime && newEndTime) {
      setHasTimeSet(true);
    } else {
      setHasTimeSet(false);
    }
  };
  
  const handleAddWithTime = () => {
    if (!draftEntry.trim()) return;
    
    if (hasTimeSet && startTime && endTime) {
      onAddEntry(selectedDay, draftEntry, startTime, endTime);
    } else {
      onAddEntry(selectedDay, draftEntry);
    }
    
    setDraftEntry('');
    setHasTimeSet(false);
    setShowTimePicker(false);
  };
  
  const handleEditClick = (entry) => {
    setEditingEntry(entry.id);
    setEditText(entry.text);
    setEditStartTime(entry.startTime || null);
    setEditEndTime(entry.endTime || null);
  };
  
  const handleSaveEdit = () => {
    if (editingEntry && editText.trim()) {
      onUpdateEntry(selectedDay, editingEntry, editText, editStartTime, editEndTime);
      setEditingEntry(null);
      setEditText('');
      setEditStartTime(null);
      setEditEndTime(null);
    }
  };
  
  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditText('');
    setEditStartTime(null);
    setEditEndTime(null);
  };
  
  const selectedDayEntries = selectedDay ? (entries[selectedDay] || []) : [];
  const selectedDayData = days.find(d => d.isoDate === selectedDay);
  
  return (
    <div className="week-view-new">
      <div className="week-days-column">
        {days.map((day) => (
          <button
            key={day.isoDate}
            type="button"
            className={`week-day-box ${COLOR_CLASS(day.count, day.isFuture)} ${
              day.isToday ? 'today' : ''
            } ${day.isFuture ? 'future' : ''} ${
              selectedDay === day.isoDate ? 'selected' : ''
            }`}
            onClick={() => handleDayClick(day)}
            onMouseEnter={(event) => onCellHover && onCellHover(event, day)}
            onMouseLeave={onCellLeave}
          >
            <div className="week-day-name">{day.dayName}</div>
            <div className="week-day-number">{day.dayNumber}</div>
          </button>
        ))}
      </div>
      
      <div className="week-entries-panel">
        {selectedDayData && (
          <>
            <div className="week-entries-header">
              <div>
                <p className="week-entries-kicker">Entries for</p>
                <h3>{selectedDayData.dayName}, {selectedDayData.monthName} {selectedDayData.dayNumber}</h3>
              </div>
            </div>
            
            <div className="week-entries-list">
              {selectedDayEntries.length === 0 && (
                <p className="empty-state">
                  Nothing planned yet — add a pocket below.
                </p>
              )}
              {selectedDayEntries.map((entry) => {
                const timeDisplay = getTimeDisplay(entry);
                const timeTooltip = getTimeRangeTooltip(entry);
                const isEditing = editingEntry === entry.id;
                
                if (isEditing) {
                  return (
                    <div key={entry.id} className="week-entry-item editing">
                      <div className="edit-mode">
                        <input
                          type="text"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="edit-text-input"
                          autoFocus
                        />
                        <div className="edit-time-picker">
                          <select
                            value={editStartTime || ''}
                            onChange={(e) => setEditStartTime(e.target.value)}
                            className="edit-time-select"
                          >
                            <option value="">All day</option>
                            {timeOptions.map((time) => (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            ))}
                          </select>
                          {editStartTime && (
                            <select
                              value={editEndTime || ''}
                              onChange={(e) => setEditEndTime(e.target.value)}
                              className="edit-time-select"
                            >
                              {timeOptions.map((time) => (
                                <option key={time} value={time}>
                                  {time}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div className="edit-actions">
                          <button className="edit-save" onClick={handleSaveEdit}>
                            Save
                          </button>
                          <button className="edit-cancel" onClick={handleCancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div
                    key={entry.id}
                    className={`week-entry-item ${entry.done ? 'done' : ''}`}
                    title={timeTooltip}
                  >
                    <div>
                      <button
                        className="entry-toggle"
                        onClick={() => onToggleEntry && onToggleEntry(selectedDay, entry.id)}
                      >
                        {entry.done ? '✔' : ''}
                      </button>
                      <span className="entry-text">{entry.text}</span>
                    </div>
                    <div className="entry-actions">
                      {timeDisplay && (
                        <span className="entry-time">{timeDisplay}</span>
                      )}
                      <button
                        className="entry-edit"
                        onClick={() => handleEditClick(entry)}
                      >
                        Edit
                      </button>
                      <button
                        className="entry-delete"
                        onClick={() => onDeleteEntry && onDeleteEntry(selectedDay, entry.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="week-add-entry">
              <input
                type="text"
                placeholder="Add pocket..."
                value={draftEntry}
                onChange={(event) => setDraftEntry(event.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    handleAddWithTime();
                  }
                }}
              />
              <button
                className={`time-icon-button ${hasTimeSet ? 'has-time' : ''}`}
                onClick={handleTimeIconClick}
                type="button"
                title="Set time"
              >
                🕐
              </button>
              <button onClick={handleAddWithTime}>Add</button>
              {showTimePicker && (
                <div className="time-picker-popup" onClick={(e) => e.stopPropagation()}>
                  <div className="time-picker-header">
                    <span>Set Time</span>
                    <button
                      className="time-picker-close"
                      onClick={() => setShowTimePicker(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="time-picker-content">
                    <div className="time-selector">
                      <label>Start:</label>
                      <select
                        value={startTime || ''}
                        onChange={(e) => handleTimeChange(e.target.value, endTime)}
                      >
                        {timeOptions.map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="time-selector">
                      <label>End:</label>
                      <select
                        value={endTime || ''}
                        onChange={(e) => handleTimeChange(startTime, e.target.value)}
                      >
                        {timeOptions.map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default WeekView;
